import type { PaymentTag, StaffRef } from './forms.js';

/**
 * The panel's own run lifecycle (implementation-plan.md §4). Atlas's status is
 * one input; the panel adds the states only staff decisions produce.
 */
export const RUN_STATUSES = [
  'starting',
  'running',
  'pausing',
  'paused',
  'awaiting_final_review',
  'review_done',
  'completed',
  'cancelled',
  'failed',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

export const TERMINAL_RUN_STATUSES = [
  'completed',
  'cancelled',
  'failed',
] as const satisfies readonly RunStatus[];

export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  starting: 'Starting',
  running: 'Running',
  pausing: 'Pausing…',
  paused: 'Paused',
  awaiting_final_review: 'Final review',
  review_done: 'Review done',
  completed: 'Completed',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

export const RUN_GROUPS = ['all', 'to_start', 'in_progress', 'completed', 'cancelled'] as const;
/** The Runs page tabs. "cancelled" holds every run that stopped without finishing: cancelled or failed. */
export type RunGroup = (typeof RUN_GROUPS)[number];
/** Runs that stopped without finishing: cancelled from the panel, or failed, quarantined or abandoned in Atlas. */
export const STOPPED_RUN_STATUSES = ['cancelled', 'failed'] as const satisfies readonly RunStatus[];

export interface StageReached {
  n: string;
  name: string;
}

export interface RunSummary {
  id: string;
  formId: string;
  company: string;
  contactEmail: string | null;
  status: RunStatus;
  atlasStatus: string | null;
  atlasRunId: string | null;
  stageReached: StageReached | null;
  stagesDone: number;
  stagesTotal: number;
  /** What the run is waiting on staff for, if anything. */
  waitingOn: string | null;
  startedAt: string;
  updatedAt: string;
}

/** A paid, reviewed form that has no run yet: the "To be started" group. */
export interface ReadyForm {
  formId: string;
  company: string;
  contactEmail: string | null;
  paymentTag: PaymentTag;
  intakeReviewedAt: string;
  receivedAt: string;
}

export interface RunCapabilities {
  pause: boolean;
  resume: boolean;
  cancelInAtlas: boolean;
  mergeLintEdit: boolean;
}

export interface RunDetail extends RunSummary {
  startedBy: StaffRef | null;
  statusReason: string | null;
  canvasBoardUrl: string | null;
  cancelledAt: string | null;
  cancelledBy: StaffRef | null;
  completedAt: string | null;
  stageSyncedAt: string | null;
  capabilities: RunCapabilities;
}

/** Stage row as a non-developer sees it: reached or not, nothing else. */
export interface StageProgress {
  n: string;
  name: string;
  stage: string | null;
  kind: string;
  reached: boolean;
  current: boolean;
  /** Stages run once per department (Analysis, Brain, Strategist): departments finished so far. */
  departmentsDone?: number;
  /** How many departments the run has, once known (after Analysis's gate); null before. */
  departmentsTotal?: number | null;
}

/** Developer view of one stage (per department for fan-out stages). */
export interface StageDetail extends StageProgress {
  /** Atlas's `pillar_id`, which holds a department id in v2. */
  departmentId: string | null;
  status: string;
  attempts: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number | null;
  durationMs: number;
  modelUsed: string | null;
  hasOutput: boolean;
  errors: { type: string; message: string; createdAt: string | null }[];
}

export interface Dashboard {
  intakesToReview: number;
  readyToStart: number;
  finalReview: number;
  emailsScheduled: number;
  emailsFailed: number;
  inProgress: RunSummary[];
}

/** A staff review point on a run. Only the final review remains in v2. */
export type ReviewKind = 'final';

export interface ReviewState {
  reviewedAt: string;
  reviewedBy: StaffRef;
  notes: string | null;
}
